import { useState, useEffect, useMemo, useCallback } from 'react'
import { Toast, Popup, Picker } from 'react-vant'
import { ArrowLeft } from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import PinyinMatch from 'pinyin-match'
import { getPhysicalTestExportPreview } from '../../api/physicalTest'
import { getCurrentAcademicYear, getAcademicYears, getAllClasses } from '../../api/academic'
import { isAdmin, isStaff, canManagePhysicalTest, isHeadTeacher } from '../../utils/permissions'

// 常用体测项目定义 (按国家小学标准顺序排列)
const TEST_ITEMS = [
  { key: 'all', label: '全部项目', unit: '分', field: 'composite_score' },
  { key: 'sit_and_reach', label: '坐位体前屈', unit: 'cm', field: 'sit_and_reach' },
  { key: 'skip_rope', label: '1分钟跳绳', unit: '次', field: 'skip_rope' },
  { key: 'run_50m', label: '50米跑', unit: '秒', field: 'run_50m' },
  { key: 'vital_capacity', label: '肺活量', unit: 'ml', field: 'vital_capacity' },
  { key: 'sit_up', label: '1分钟仰卧起坐', unit: '次', field: 'sit_up' },
  { key: 'bmi', label: '体重指数 (BMI)', unit: 'kg/m²', field: 'bmi' },
  { key: 'run_50m_8x', label: '50米×8往返跑', unit: '秒', field: 'run_50m_8x' },
  { key: 'standing_long_jump', label: '立定跳远', unit: 'cm', field: 'standing_long_jump' },
]

const GRADE_OPTIONS = [
  '全部年级', '一年级', '二年级', '三年级', '四年级', '五年级', '六年级', '七年级', '八年级', '九年级'
]

const STATUS_OPTIONS = [
  { label: '全部状态', value: 'all' },
  { label: '优秀 (≥90)', value: 'A' },
  { label: '良好 (80-89)', value: 'B' },
  { label: '及格 (60-79)', value: 'C' },
  { label: '不及格 (<60)', value: 'D' },
  { label: '待补测/缺考', value: 'untested' },
]

/**
 * 仪表盘环形进度圈
 */
function CircularGauge({ percent = 0, label, color = '#34D399', size = 76, decimals = 0 }) {
  let displayVal = '0'
  const num = Number(percent)
  if (!isNaN(num)) {
    displayVal = decimals > 0 ? num.toFixed(decimals) : Math.round(num).toString()
  } else {
    displayVal = String(percent)
  }
  const isSmallText = displayVal.length >= 5

  return (
    <div
      className="flex flex-col items-center justify-center rounded-full border-[4px] relative"
      style={{
        width: size,
        height: size,
        borderColor: color,
        boxShadow: `0 0 12px ${color}33`,
      }}
    >
      <span className={`font-extrabold text-white leading-tight ${isSmallText ? 'text-[12px]' : 'text-[15px]'}`}>
        {displayVal}%
      </span>
      <span className="text-[10px] font-bold mt-0.5 tracking-tight" style={{ color }}>
        {label}
      </span>
    </div>
  )
}

/**
 * 单项折叠卡片微型进度圈
 */
function MiniGauge({ percent = 0, label, color = '#34D399', decimals = 0 }) {
  let displayVal = '0'
  const num = Number(percent)
  if (!isNaN(num)) {
    displayVal = decimals > 0 ? num.toFixed(decimals) : Math.round(num).toString()
  } else {
    displayVal = String(percent)
  }
  return (
    <div className="flex flex-col items-center">
      <div
        className="w-11 h-11 rounded-full border-[2.5px] flex items-center justify-center bg-white/5"
        style={{ borderColor: color }}
      >
        <span className="text-[11px] font-bold text-white">{displayVal}%</span>
      </div>
      <span className="text-[9.5px] font-medium mt-1" style={{ color }}>{label}</span>
    </div>
  )
}

/**
 * 解析单项评级
 */
const parseStudentItemScores = (student) => {
  if (!student || !student.item_scores) return null
  if (typeof student.item_scores === 'object') return student.item_scores
  if (typeof student.item_scores === 'string') {
    try {
      return JSON.parse(student.item_scores)
    } catch {
      return null
    }
  }
  return null
}

const getItemGrade = (student, itemKey, itemField) => {
  if (!student) return null
  const parsedScores = parseStudentItemScores(student)
  if (parsedScores) {
    const itemObj = parsedScores[itemKey] || parsedScores[itemField]
    if (itemObj) {
      const g = itemObj.grade
      if (g === '优秀' || g === 'A') return 'A'
      if (g === '良好' || g === 'B') return 'B'
      if (g === '及格' || g === 'C') return 'C'
      if (g === '不及格' || g === 'D') return 'D'

      const sc = Number(itemObj.score)
      if (!isNaN(sc) && sc > 0) {
        if (sc >= 90) return 'A'
        if (sc >= 80) return 'B'
        if (sc >= 60) return 'C'
        return 'D'
      }
    }
  }

  const rawVal = student[itemField]
  if (rawVal === null || rawVal === undefined || rawVal === '') {
    return null
  }
  const val = Number(rawVal)
  if (isNaN(val)) return student.grade_level || 'C'

  if (itemKey === 'bmi') {
    if (val >= 14.2 && val <= 20.0) return 'A'
    if ((val >= 13.0 && val < 14.2) || (val > 20.0 && val <= 22.5)) return 'B'
    if (val > 22.5 && val <= 25.0) return 'C'
    return 'D'
  }
  if (itemKey === 'sit_and_reach') {
    if (val >= 15.0) return 'A'
    if (val >= 10.0) return 'B'
    if (val >= 1.0) return 'C'
    return 'D'
  }
  if (itemKey === 'skip_rope') {
    if (val >= 140) return 'A'
    if (val >= 115) return 'B'
    if (val >= 75) return 'C'
    return 'D'
  }
  if (itemKey === 'run_50m') {
    if (val <= 8.8) return 'A'
    if (val <= 9.6) return 'B'
    if (val <= 11.2) return 'C'
    return 'D'
  }
  if (itemKey === 'vital_capacity') {
    if (val >= 2200) return 'A'
    if (val >= 1800) return 'B'
    if (val >= 1300) return 'C'
    return 'D'
  }
  if (itemKey === 'sit_up') {
    if (val >= 44) return 'A'
    if (val >= 37) return 'B'
    if (val >= 24) return 'C'
    return 'D'
  }
  if (itemKey === 'run_50m_8x') {
    if (val <= 115) return 'A'
    if (val <= 125) return 'B'
    if (val <= 145) return 'C'
    return 'D'
  }
  if (itemKey === 'standing_long_jump') {
    if (val >= 170) return 'A'
    if (val >= 150) return 'B'
    if (val >= 120) return 'C'
    return 'D'
  }

  return student.grade_level || 'C'
}

export default function PhysicalTestAnalytics() {
  const navigate = useNavigate()
  const { userInfo } = useSelector(state => state.user)

  // 权限检查
  const hasAccess = isAdmin(userInfo) || isStaff(userInfo) || canManagePhysicalTest(userInfo) || isHeadTeacher(userInfo)

  const [loading, setLoading] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [viewMode, setViewMode] = useState('student') // 'student' (学生明细) | 'class' (班级大盘)

  // 筛选状态
  const [academicYears, setAcademicYears] = useState([])
  const [selectedYear, setSelectedYear] = useState('')
  const [pickerModalType, setPickerModalType] = useState(null) // 'year' | 'item' | 'grade' | 'class' | 'status'

  const [allClasses, setAllClasses] = useState([])
  const [selectedGrade, setSelectedGrade] = useState('全部年级')
  const [selectedClassId, setSelectedClassId] = useState(null)
  const [selectedItemKey, setSelectedItemKey] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [isSearching, setIsSearching] = useState(false)

  // 核心数据源
  const [rawStudentList, setRawStudentList] = useState([])

  // 初始化加载学年与班级
  useEffect(() => {
    const initFilters = async () => {
      try {
        const [currRes, yearsRes, classesRes] = await Promise.allSettled([
          getCurrentAcademicYear(),
          getAcademicYears(),
          getAllClasses({ page_size: 1000 }),
        ])

        let yearList = []
        if (yearsRes.status === 'fulfilled' && yearsRes.value) {
          const val = yearsRes.value
          if (Array.isArray(val)) yearList = val
          else if (val.options) yearList = val.options.map(o => String(o.value || o))
          else if (val.years) yearList = val.years
        }

        if (!yearList || yearList.length === 0) {
          const now = new Date()
          const yr = now.getFullYear()
          yearList = [now.getMonth() >= 7 ? `${yr}-${yr + 1}` : `${yr - 1}-${yr}`]
        }
        setAcademicYears(yearList)

        let activeYear = localStorage.getItem('current_academic_year')
        if (!activeYear || !yearList.includes(activeYear)) {
          if (currRes.status === 'fulfilled' && currRes.value?.current_academic_year) {
            activeYear = currRes.value.current_academic_year
          } else {
            activeYear = yearList[0]
          }
        }
        setSelectedYear(activeYear)

        if (classesRes.status === 'fulfilled' && classesRes.value) {
          const list = classesRes.value.items || classesRes.value.data || []
          setAllClasses(list)
        }
      } catch (err) {
        console.warn('初始化体测数据看板失败:', err)
      }
    }
    initFilters()
  }, [])

  // 当学年改变时拉取体测数据
  const loadData = useCallback(async () => {
    if (!selectedYear) return
    setLoading(true)
    try {
      const res = await getPhysicalTestExportPreview({
        academic_year: selectedYear,
        include_statistics: true,
      })
      if (res && res.data) {
        setRawStudentList(res.data)
      } else {
        setRawStudentList([])
      }
    } catch (err) {
      console.error('拉取体测大盘数据失败:', err)
      Toast.fail('获取体测大盘数据失败')
    } finally {
      setLoading(false)
    }
  }, [selectedYear])

  useEffect(() => {
    loadData()
  }, [loadData])

  // 可用班级
  const availableClasses = useMemo(() => {
    if (!selectedGrade || selectedGrade === '全部年级') {
      return allClasses
    }
    return allClasses.filter(c => c.grade === selectedGrade)
  }, [allClasses, selectedGrade])

  // 基准大盘人群
  const cohortStudents = useMemo(() => {
    return rawStudentList.filter(s => {
      if (selectedGrade !== '全部年级') {
        if (s.grade !== selectedGrade && (!s.class_name || !s.class_name.includes(selectedGrade))) {
          return false
        }
      }
      if (selectedClassId !== null) {
        if (s.class_id !== selectedClassId && s.class_code !== selectedClassId) {
          const target = allClasses.find(c => c.id === selectedClassId)
          if (target && s.class_name !== target.class_name) {
            return false
          }
        }
      }
      if (searchKeyword.trim()) {
        const kw = searchKeyword.trim().toLowerCase()
        const name = (s.student_name || '').toLowerCase()
        const eduId = (s.education_id || '').toLowerCase()
        const sno = (s.student_no || '').toLowerCase()

        let match = name.includes(kw) || eduId.includes(kw) || sno.includes(kw)
        if (!match && s.student_name) {
          try {
            match = !!PinyinMatch.match(s.student_name, searchKeyword.trim())
          } catch {
            // ignore
          }
        }
        if (!match) return false
      }
      return true
    })
  }, [rawStudentList, selectedGrade, selectedClassId, searchKeyword, allClasses])

  // 核心总评指标统计
  const currentStats = useMemo(() => {
    const total = cohortStudents.length
    const testedList = cohortStudents.filter(
      s => s.total_score !== null && s.total_score !== undefined
    )
    const testedCount = testedList.length
    const entryProgress = total > 0 ? Math.round((testedCount / total) * 100) : 0

    const excCount = testedList.filter(s => s.grade_level === 'A').length
    const goodCount = testedList.filter(s => s.grade_level === 'B').length
    const passCount = testedList.filter(s => s.grade_level === 'C').length
    const failCount = testedList.filter(s => s.grade_level === 'D').length

    const denominator = testedCount > 0 ? testedCount : 1
    const excGoodNum = testedCount > 0 ? ((excCount + goodCount) / denominator) * 100 : 0
    const passNum = testedCount > 0 ? ((excCount + goodCount + passCount) / denominator) * 100 : 0
    const excellentGoodRate = Number(excGoodNum.toFixed(2))
    const passRate = passNum >= 100 ? 100 : Number(passNum.toFixed(2))

    return {
      total,
      testedCount,
      entryProgress,
      excellentGoodRate,
      passRate,
      excCount,
      goodCount,
      passCount,
      failCount,
    }
  }, [cohortStudents])

  // 单项统计汇总
  const itemAnalyticsList = useMemo(() => {
    const coreItems = [
      { key: 'sit_and_reach', label: '坐位体前屈', unit: 'cm', field: 'sit_and_reach' },
      { key: 'skip_rope', label: '1分钟跳绳', unit: '次', field: 'skip_rope' },
      { key: 'run_50m', label: '50米跑', unit: '秒', field: 'run_50m' },
      { key: 'vital_capacity', label: '肺活量', unit: 'ml', field: 'vital_capacity' },
      { key: 'sit_up', label: '1分钟仰卧起坐', unit: '次', field: 'sit_up' },
      { key: 'bmi', label: '体重指数 (BMI)', unit: 'kg/m²', field: 'bmi' },
    ]

    const has50m8x = cohortStudents.some(
      s => s.run_50m_8x !== null && s.run_50m_8x !== undefined && s.run_50m_8x !== ''
    )
    if (has50m8x) {
      coreItems.push({
        key: 'run_50m_8x',
        label: '50米×8往返跑',
        unit: '秒',
        field: 'run_50m_8x',
      })
    }

    const cohortTotal = cohortStudents.length

    return coreItems.map(item => {
      const valid = cohortStudents.filter(
        s => s[item.field] !== null && s[item.field] !== undefined && s[item.field] !== ''
      )
      const count = valid.length
      const sum = valid.reduce((acc, curr) => acc + (Number(curr[item.field]) || 0), 0)
      const avg = count > 0 ? (sum / count).toFixed(1) : '-'
      const entryProgress = cohortTotal > 0 ? Math.round((count / cohortTotal) * 100) : 0

      let excCount = 0
      let goodCount = 0
      let passCount = 0
      let failCount = 0

      valid.forEach(s => {
        const g = getItemGrade(s, item.key, item.field)
        if (g === 'A') excCount += 1
        else if (g === 'B') goodCount += 1
        else if (g === 'C') passCount += 1
        else if (g === 'D') failCount += 1
      })

      const denominator = count > 0 ? count : 1
      const excGoodNum = count > 0 ? ((excCount + goodCount) / denominator) * 100 : 0
      const passNum = count > 0 ? ((excCount + goodCount + passCount) / denominator) * 100 : 0

      return {
        ...item,
        testedCount: count,
        totalCount: cohortTotal,
        average: avg,
        entryProgress,
        excellentGoodRate: Number(excGoodNum.toFixed(2)),
        passRate: passNum >= 100 ? 100 : Number(passNum.toFixed(2)),
        excCount,
        goodCount,
        passCount,
        failCount,
      }
    })
  }, [cohortStudents])

  // 班级大盘汇总数据
  const classBreakdownList = useMemo(() => {
    const classMap = {}
    cohortStudents.forEach(s => {
      const cId = s.class_id || s.class_code || s.class_name || '未分班'
      const cName = s.class_name || '主校区'
      if (!classMap[cId]) {
        classMap[cId] = {
          classId: cId,
          className: cName,
          grade: s.grade || '',
          students: [],
        }
      }
      classMap[cId].students.push(s)
    })

    return Object.values(classMap).map(c => {
      const total = c.students.length
      const tested = c.students.filter(s => s.total_score !== null && s.total_score !== undefined)
      const testedCount = tested.length
      const progress = total > 0 ? Math.round((testedCount / total) * 100) : 0

      const sumScore = tested.reduce((acc, curr) => acc + (Number(curr.composite_score ?? curr.total_score) || 0), 0)
      const avgScore = testedCount > 0 ? (sumScore / testedCount).toFixed(1) : '-'

      const excGoodCount = tested.filter(s => s.grade_level === 'A' || s.grade_level === 'B').length
      const passCount = tested.filter(s => ['A', 'B', 'C'].includes(s.grade_level)).length
      const denom = testedCount > 0 ? testedCount : 1

      return {
        ...c,
        total,
        testedCount,
        progress,
        avgScore,
        excellentGoodRate: Number(((excGoodCount / denom) * 100).toFixed(1)),
        passRate: Number(((passCount / denom) * 100).toFixed(1)),
      }
    }).sort((a, b) => b.progress - a.progress)
  }, [cohortStudents])

  // 学生列表过滤
  const filteredStudents = useMemo(() => {
    return cohortStudents.filter(s => {
      if (selectedStatus !== 'all') {
        if (selectedItemKey === 'all') {
          if (selectedStatus === 'untested') {
            if (s.total_score !== null && s.grade_level) return false
          } else {
            if (s.grade_level !== selectedStatus) return false
          }
        } else {
          const itemDef = TEST_ITEMS.find(it => it.key === selectedItemKey)
          if (itemDef && itemDef.field) {
            const val = s[itemDef.field]
            if (selectedStatus === 'untested') {
              if (val !== null && val !== undefined && val !== '') return false
            } else {
              const g = getItemGrade(s, itemDef.key, itemDef.field)
              if (g !== selectedStatus) return false
            }
          }
        }
      }
      return true
    })
  }, [cohortStudents, selectedStatus, selectedItemKey])

  // 排序后的学生列表 (默认总分倒序，选择单项时按单项数值倒序/正序)
  const sortedStudents = useMemo(() => {
    const list = [...filteredStudents]
    list.sort((a, b) => {
      if (selectedItemKey !== 'all') {
        const itemDef = TEST_ITEMS.find(it => it.key === selectedItemKey)
        if (itemDef && itemDef.field) {
          const valA = a[itemDef.field]
          const valB = b[itemDef.field]
          const hasA = valA !== null && valA !== undefined && valA !== ''
          const hasB = valB !== null && valB !== undefined && valB !== ''
          if (!hasA && !hasB) return 0
          if (!hasA) return 1
          if (!hasB) return -1
          const numA = Number(valA) || 0
          const numB = Number(valB) || 0
          if (selectedItemKey === 'run_50m' || selectedItemKey === 'run_50m_8x') {
            return numA - numB
          }
          return numB - numA
        }
      }
      const scoreA = a.composite_score ?? a.total_score ?? -1
      const scoreB = b.composite_score ?? b.total_score ?? -1
      return scoreB - scoreA
    })
    return list
  }, [filteredStudents, selectedItemKey])

  const getStudentProgress = (student) => {
    const fields = ['bmi', 'vital_capacity', 'run_50m', 'sit_and_reach', 'skip_rope', 'sit_up']
    let completed = 0
    fields.forEach(f => {
      if (student[f] !== null && student[f] !== undefined && student[f] !== '') {
        completed += 1
      }
    })
    return { completed, total: 6 }
  }

  const getGradeBadge = (grade) => {
    switch (grade) {
      case 'A':
        return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-[#e8f9f0] text-[#00b578]">优秀</span>
      case 'B':
        return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-[#e8f6f0] text-[#056b4f]">良好</span>
      case 'C':
        return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-[#fff7e6] text-[#fa8c16]">及格</span>
      case 'D':
        return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-[#fef2f2] text-[#e11d48]">不及格</span>
      default:
        return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-[#f1f5f9] text-[#94a3b8]">待测</span>
    }
  }

  // 点击班级下钻查看学生
  const handleDrilldownClass = (classId) => {
    setSelectedClassId(classId)
    setViewMode('student')
  }

  // 重置全部筛选条件
  const handleResetFilters = () => {
    setSelectedGrade('全部年级')
    setSelectedClassId(null)
    setSelectedItemKey('all')
    setSelectedStatus('all')
    setSearchKeyword('')
    setIsSearching(false)
    Toast.info('已重置所有筛选')
  }

  const isFilterActive = selectedGrade !== '全部年级' || selectedClassId !== null || selectedStatus !== 'all' || selectedItemKey !== 'all' || searchKeyword.trim() !== ''

  // 弹窗 Picker 数据列构建
  const pickerColumns = useMemo(() => {
    switch (pickerModalType) {
      case 'year':
        return academicYears.map(y => ({ text: `${y}学年`, value: y }))
      case 'item':
        return TEST_ITEMS.map(it => ({ text: it.label, value: it.key }))
      case 'grade':
        return GRADE_OPTIONS.map(g => ({ text: g, value: g }))
      case 'class':
        return [
          { text: '全部班级', value: null },
          ...availableClasses.map(c => ({ text: c.class_name, value: c.id })),
        ]
      case 'status':
        return STATUS_OPTIONS.map(st => ({ text: st.label, value: st.value }))
      default:
        return []
    }
  }, [pickerModalType, academicYears, availableClasses])

  const pickerTitle = useMemo(() => {
    switch (pickerModalType) {
      case 'year': return '选择学年'
      case 'item': return '选择测试项目'
      case 'grade': return '选择年级'
      case 'class': return '选择班级'
      case 'status': return '选择评定状态'
      default: return ''
    }
  }, [pickerModalType])

  const handlePickerConfirm = (val) => {
    switch (pickerModalType) {
      case 'year':
        setSelectedYear(val)
        localStorage.setItem('current_academic_year', val)
        break
      case 'item':
        setSelectedItemKey(val)
        break
      case 'grade':
        setSelectedGrade(val)
        setSelectedClassId(null)
        break
      case 'class':
        setSelectedClassId(val)
        break
      case 'status':
        setSelectedStatus(val)
        break
      default:
        break
    }
    setPickerModalType(null)
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] text-[#0f172a] pb-24 font-sans select-none">
      {/* 1. 顶栏导航 (星翼云蓝阶页眉) */}
      <div
        className="sticky top-0 z-30 px-4 pt-3 pb-3.5 shadow-sm"
        style={{ background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)' }}
      >
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>

          <div className="text-center">
            <h1 className="text-[17.5px] font-bold text-white tracking-wide leading-tight">体测数据看板</h1>
            <p className="text-[11.5px] text-white/80 font-normal mt-0.5">多维统计 · 成绩大盘 · 班级下钻</p>
          </div>

          <button
            onClick={() => navigate('/physical-test/calculator')}
            className="w-9 h-9 rounded-full bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="4" y="2" width="16" height="20" rx="2"></rect>
              <line x1="8" y1="6" x2="16" y2="6"></line>
              <line x1="16" y1="14" x2="16" y2="18"></line>
              <path d="M16 10h.01"></path>
              <path d="M12 10h.01"></path>
              <path d="M8 10h.01"></path>
              <path d="M12 14h.01"></path>
              <path d="M8 14h.01"></path>
              <path d="M12 18h.01"></path>
              <path d="M8 18h.01"></path>
            </svg>
          </button>
        </div>
      </div>

      <div className="px-3.5 pt-3 space-y-3 max-w-lg mx-auto">
        {/* 2. 沉浸式深空总评数据大盘 (与 App Image 1 完全对齐) */}
        <div
          className="rounded-2xl p-4 shadow-[0_8px_24px_rgba(15,23,42,0.18)] text-white relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #052e21 0%, #0b3d2c 50%, #04231a 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {/* 大盘顶栏 */}
          <div className="flex items-center justify-between pb-3 mb-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#34d399] animate-pulse"></span>
              <span className="text-[16px] font-bold text-white tracking-wide">总评</span>
            </div>

            <div className="flex items-center gap-2.5">
              <span className="text-[13px] font-medium text-white/80">
                {currentStats.testedCount} / {currentStats.total} 人
              </span>
              <button
                type="button"
                onClick={() => setIsExpanded(prev => !prev)}
                className="px-2.5 py-1 rounded-full bg-white/10 active:bg-white/20 text-[11px] font-semibold text-white/90 transition-colors"
              >
                {isExpanded ? '收起 ∧' : '展开 ∨'}
              </button>
            </div>
          </div>

          {/* 三大核心环形仪表盘 */}
          <div className="flex items-center justify-around py-1">
            <CircularGauge
              percent={currentStats.entryProgress}
              label="录入进度"
              color="#34D399"
              decimals={0}
            />
            <CircularGauge
              percent={currentStats.excellentGoodRate}
              label="优良率"
              color="#2DD4BF"
              decimals={2}
            />
            <CircularGauge
              percent={currentStats.passRate}
              label="及格率"
              color="#FA8C16"
              decimals={currentStats.passRate === 100 ? 0 : 2}
            />
          </div>

          {/* 数据细分统计分布 */}
          <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-white/10 text-[12px]">
            <div className="flex flex-col justify-center">
              <span className="font-bold text-white text-[12.5px]">有效 {currentStats.testedCount} 人</span>
              <span className="text-[10px] text-white/50 mt-0.5">不含未测、免测</span>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#34D399]"></span>
                <span className="text-white/80">优秀 {currentStats.excCount}人</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FA8C16]"></span>
                <span className="text-white/80">及格 {currentStats.passCount}人</span>
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#34D399]"></span>
                <span className="text-white/80">良好 {currentStats.goodCount}人</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F87171]"></span>
                <span className="text-white/80">不及格 {currentStats.failCount}人</span>
              </div>
            </div>
          </div>

          {/* 展开后的单项数据大盘 */}
          {isExpanded && (
            <div className="mt-4 pt-3 border-t border-white/15 space-y-2.5 max-h-[360px] overflow-y-auto pr-1">
              <div className="text-[12.5px] font-bold text-[#a7f3d0] flex items-center gap-1.5 mb-2">
                <span>单项数据明细 (以总评大盘为分母)</span>
              </div>
              {itemAnalyticsList.map(item => (
                <div
                  key={item.key}
                  className="p-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <div className="text-[13px] font-bold text-white flex items-center gap-1.5">
                      <span>{item.label}</span>
                      <span className="text-[11px] text-white/60 font-normal">均值: {item.average} {item.unit}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1.5 text-[10.5px] text-white/70">
                      <span>优秀: {item.excCount}人</span>
                      <span>良好: {item.goodCount}人</span>
                      <span>及格: {item.passCount}人</span>
                      <span>不及格: {item.failCount}人</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <MiniGauge percent={item.entryProgress} label="进度" color="#34D399" />
                    <MiniGauge percent={item.excellentGoodRate} label="优良率" color="#2DD4BF" decimals={1} />
                    <MiniGauge percent={item.passRate} label="及格率" color="#FA8C16" decimals={1} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 3. 多维筛选控制台 (三阶自适应架构：模式切换 + 学年项目 + 细项筛选，彻底杜绝文字溢出) */}
        <div className="bg-white rounded-2xl p-3 shadow-sm border border-[#e2e8f0] space-y-2.5">
          {/* 第 1 阶：视图模式全宽切换 (学生明细 / 班级大盘) */}
          <div className="grid grid-cols-2 p-1 bg-[#f1f5f9] rounded-xl gap-1">
            <button
              type="button"
              onClick={() => setViewMode('student')}
              className={`py-2 rounded-lg text-[13px] font-bold transition-all flex items-center justify-center gap-1.5 ${
                viewMode === 'student'
                  ? 'bg-[#056b4f] text-white shadow-sm'
                  : 'text-[#64748b] hover:text-[#0f172a]'
              }`}
            >
              <span>学生明细</span>
              <span className={`text-[10.5px] px-1.5 py-0.2 rounded-full font-semibold ${
                viewMode === 'student' ? 'bg-white/20 text-white' : 'bg-white text-[#64748b]'
              }`}>
                {filteredStudents.length}人
              </span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('class')}
              className={`py-2 rounded-lg text-[13px] font-bold transition-all flex items-center justify-center gap-1.5 ${
                viewMode === 'class'
                  ? 'bg-[#056b4f] text-white shadow-sm'
                  : 'text-[#64748b] hover:text-[#0f172a]'
              }`}
            >
              <span>班级大盘</span>
              <span className={`text-[10.5px] px-1.5 py-0.2 rounded-full font-semibold ${
                viewMode === 'class' ? 'bg-white/20 text-white' : 'bg-white text-[#64748b]'
              }`}>
                {classBreakdownList.length}个班
              </span>
            </button>
          </div>

          {/* 第 2 阶：学年与项目下拉选择 (双列等宽 50% / 50%) */}
          <div className="grid grid-cols-2 gap-2">
            {/* 学年下拉选择按钮 */}
            <button
              type="button"
              onClick={() => setPickerModalType('year')}
              className="flex items-center justify-between px-3 py-2 rounded-xl bg-[#f8fafc] border border-[#e2e8f0] text-[13px] font-bold text-[#0f172a] active:bg-[#f1f5f9] transition-all"
            >
              <span className="truncate">{selectedYear ? `${selectedYear}学年` : '选择学年'}</span>
              <span className="text-[10px] text-[#64748b] ml-1 shrink-0">▼</span>
            </button>

            {/* 项目下拉选择按钮 */}
            <button
              type="button"
              onClick={() => setPickerModalType('item')}
              className={`flex items-center justify-between px-3 py-2 rounded-xl border text-[13px] font-bold active:bg-[#f1f5f9] transition-all ${
                selectedItemKey !== 'all'
                  ? 'bg-[#f0fdf4] border-[#056b4f] text-[#056b4f]'
                  : 'bg-[#f8fafc] border-[#e2e8f0] text-[#0f172a]'
              }`}
            >
              <span className="truncate">
                {TEST_ITEMS.find(it => it.key === selectedItemKey)?.label || '全部项目'}
              </span>
              <span className="text-[10px] text-[#64748b] ml-1 shrink-0">▼</span>
            </button>
          </div>

          {/* 第 3 阶：细项筛选 (年级 + 班级 + 状态 + 搜索开关) */}
          {viewMode === 'student' ? (
            <div className="flex items-center gap-1.5">
              {/* 年级下拉胶囊 */}
              <button
                type="button"
                onClick={() => setPickerModalType('grade')}
                className={`flex-1 min-w-0 px-2 py-1.5 rounded-lg border text-[12px] flex items-center justify-center gap-1 transition-all ${
                  selectedGrade !== '全部年级'
                    ? 'bg-[#056b4f] text-white border-[#056b4f] font-bold shadow-sm'
                    : 'bg-[#f8fafc] text-[#334155] border-[#e2e8f0] font-medium'
                }`}
              >
                <span className="truncate">{selectedGrade}</span>
                <span className="text-[8px] opacity-70 shrink-0">▼</span>
              </button>

              {/* 班级下拉胶囊 */}
              <button
                type="button"
                onClick={() => setPickerModalType('class')}
                className={`flex-1 min-w-0 px-2 py-1.5 rounded-lg border text-[12px] flex items-center justify-center gap-1 transition-all ${
                  selectedClassId !== null
                    ? 'bg-[#056b4f] text-white border-[#056b4f] font-bold shadow-sm'
                    : 'bg-[#f8fafc] text-[#334155] border-[#e2e8f0] font-medium'
                }`}
              >
                <span className="truncate">
                  {selectedClassId !== null
                    ? (allClasses.find(c => c.id === selectedClassId)?.class_name || '已选班级')
                    : '全部班级'}
                </span>
                <span className="text-[8px] opacity-70 shrink-0">▼</span>
              </button>

              {/* 状态下拉胶囊 */}
              <button
                type="button"
                onClick={() => setPickerModalType('status')}
                className={`flex-1 min-w-0 px-2 py-1.5 rounded-lg border text-[12px] flex items-center justify-center gap-1 transition-all ${
                  selectedStatus !== 'all'
                    ? 'bg-[#056b4f] text-white border-[#056b4f] font-bold shadow-sm'
                    : 'bg-[#f8fafc] text-[#334155] border-[#e2e8f0] font-medium'
                }`}
              >
                <span className="truncate">
                  {STATUS_OPTIONS.find(s => s.value === selectedStatus)?.label.split(' ')[0] || '全部状态'}
                </span>
                <span className="text-[8px] opacity-70 shrink-0">▼</span>
              </button>

              {/* 搜索开关按钮 */}
              <button
                type="button"
                onClick={() => setIsSearching(prev => !prev)}
                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border transition-all ${
                  isSearching
                    ? 'bg-[#e11d48] text-white border-[#e11d48]'
                    : 'bg-[#ecfdf5] text-[#00b578] border-[#a7f3d0] active:bg-[#d1fae5]'
                }`}
                title="搜索学生"
              >
                {isSearching ? (
                  <span className="text-[13px] font-bold leading-none">✕</span>
                ) : (
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                )}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {/* 年级限定胶囊 (班级大盘模式) */}
              <button
                type="button"
                onClick={() => setPickerModalType('grade')}
                className={`flex-1 px-3 py-1.5 rounded-lg border text-[12px] flex items-center justify-between transition-all ${
                  selectedGrade !== '全部年级'
                    ? 'bg-[#056b4f] text-white border-[#056b4f] font-bold shadow-sm'
                    : 'bg-[#f8fafc] text-[#334155] border-[#e2e8f0] font-medium'
                }`}
              >
                <span>限定年级: {selectedGrade}</span>
                <span className="text-[9px] opacity-70">▼</span>
              </button>
            </div>
          )}

          {/* 搜索框 (展开态) */}
          {isSearching && (
            <div className="relative flex items-center pt-1 border-t border-[#f1f5f9]">
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                placeholder="搜索姓名 / 拼音简拼 (如ZS) / 学籍号 / 学号"
                className="w-full pl-8 pr-8 py-1.5 rounded-xl bg-[#f8fafc] border border-[#e2e8f0] text-[12.5px] text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:border-[#056b4f]"
                autoFocus
              />
              <svg className="w-4 h-4 text-[#94a3b8] absolute left-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              {searchKeyword && (
                <button
                  type="button"
                  onClick={() => setSearchKeyword('')}
                  className="w-4 h-4 rounded-full bg-[#cbd5e1] text-white absolute right-2.5 flex items-center justify-center text-[10px]"
                >
                  ×
                </button>
              )}
            </div>
          )}

          {/* 筛选重置快捷提示条 */}
          {isFilterActive && (
            <div className="flex items-center justify-between pt-1 border-t border-[#f1f5f9] text-[11px] text-[#64748b]">
              <span>当前已应用多维过滤</span>
              <button
                type="button"
                onClick={handleResetFilters}
                className="text-[#056b4f] font-bold active:underline"
              >
                重置所有筛选 ↺
              </button>
            </div>
          )}
        </div>
        
        {/* 4. 视图内容区 */}
        {viewMode === 'class' ? (
          /* ===== 班级大盘卡片列表 ===== */
          <div className="space-y-2.5">
            <div className="flex items-center justify-between text-[12px] text-[#64748b] px-1 font-semibold">
              <span>共计 {classBreakdownList.length} 个班级</span>
              <span>点击班级可直接下钻查看学生</span>
            </div>

            {classBreakdownList.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center text-[#94a3b8] text-[13px] border border-[#e2e8f0]">
                暂无符合筛选条件的班级数据
              </div>
            ) : (
              classBreakdownList.map(c => (
                <div
                  key={c.classId}
                  onClick={() => handleDrilldownClass(c.classId)}
                  className="bg-white rounded-2xl p-4 shadow-sm border border-[#e2e8f0] active:border-[#056b4f] transition-all cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[15px] font-bold text-[#0f172a]">{c.className}</span>
                      {c.grade && (
                        <span className="px-1.5 py-0.5 rounded text-[10.5px] bg-[#f1f5f9] text-[#64748b]">
                          {c.grade}
                        </span>
                      )}
                    </div>
                    <span className="text-[12.5px] font-bold text-[#056b4f]">
                      {c.testedCount} / {c.total} 人 ({c.progress}%)
                    </span>
                  </div>

                  {/* 进度条 */}
                  <div className="w-full bg-[#f1f5f9] h-2 rounded-full overflow-hidden mb-3">
                    <div
                      className="bg-gradient-to-r from-[#056b4f] to-[#00b578] h-full rounded-full transition-all duration-300"
                      style={{ width: `${c.progress}%` }}
                    />
                  </div>

                  {/* 三大指标 */}
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-[#f1f5f9] text-center text-[12px]">
                    <div>
                      <div className="text-[11px] text-[#94a3b8]">平均分</div>
                      <div className="text-[14px] font-bold text-[#0f172a] mt-0.5">{c.avgScore}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-[#94a3b8]">优良率</div>
                      <div className="text-[14px] font-bold text-[#00b578] mt-0.5">{c.excellentGoodRate}%</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-[#94a3b8]">及格率</div>
                      <div className="text-[14px] font-bold text-[#fa8c16] mt-0.5">{c.passRate}%</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* ===== 学生明细列表 ===== */
          <div className="space-y-2">
            <div className="flex items-center justify-between text-[12px] text-[#64748b] px-1 font-semibold">
              <span>当前筛选结果：{sortedStudents.length} 名学生</span>
              {selectedClassId !== null && (
                <button
                  type="button"
                  onClick={() => setSelectedClassId(null)}
                  className="text-[#056b4f] active:underline font-bold"
                >
                  清除班级筛选 ×
                </button>
              )}
            </div>

            {sortedStudents.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center text-[#94a3b8] text-[13px] border border-[#e2e8f0]">
                {loading ? '正在加载体测数据...' : '未匹配到任何学生记录'}
              </div>
            ) : (
              sortedStudents.map((stu, idx) => {
                const rank = idx + 1
                const isMale = stu.gender_code === '1' || stu.gender === '男' || stu.gender === 'male'
                const { completed, total } = getStudentProgress(stu)
                const progressPct = `${(completed / total) * 100}%`

                let displayScore = '-'
                let displayGrade = stu.grade_level
                if (selectedItemKey !== 'all') {
                  const itDef = TEST_ITEMS.find(i => i.key === selectedItemKey)
                  if (itDef && stu[itDef.field] !== null && stu[itDef.field] !== undefined && stu[itDef.field] !== '') {
                    displayScore = `${stu[itDef.field]} ${itDef.unit}`
                    displayGrade = getItemGrade(stu, itDef.key, itDef.field)
                  } else {
                    displayScore = '未测'
                    displayGrade = null
                  }
                } else {
                  const rawScore = stu.composite_score ?? stu.total_score
                  displayScore = rawScore !== null && rawScore !== undefined ? Number(rawScore).toFixed(1) : '-'
                }

                return (
                  <div
                    key={stu.id || `${stu.student_id}_${idx}`}
                    onClick={() => {
                      if (stu.id) {
                        navigate(`/physical-test/detail/${stu.id}`)
                      } else if (stu.student_id) {
                        navigate(`/teacher/student-detail/${stu.student_id}`)
                      }
                    }}
                    className="bg-white rounded-2xl p-3.5 shadow-sm border border-[#e2e8f0] flex items-center gap-3 active:bg-[#f8fafc] transition-all cursor-pointer"
                  >
                    {/* 排名徽标 */}
                    <div className="w-7 text-center">
                      {rank === 1 ? (
                        <span className="w-6 h-6 rounded-full bg-[#fef3c7] text-[#d97706] font-extrabold text-[12px] flex items-center justify-center">1</span>
                      ) : rank === 2 ? (
                        <span className="w-6 h-6 rounded-full bg-[#f1f5f9] text-[#64748b] font-extrabold text-[12px] flex items-center justify-center">2</span>
                      ) : rank === 3 ? (
                        <span className="w-6 h-6 rounded-full bg-[#fed7aa] text-[#c2410c] font-extrabold text-[12px] flex items-center justify-center">3</span>
                      ) : (
                        <span className="text-[12px] font-bold text-[#94a3b8]">{rank}</span>
                      )}
                    </div>

                    {/* 学生信息 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[12px] ${isMale ? 'text-[#056b4f]' : 'text-[#ec4899]'}`}>
                          {isMale ? '♂' : '♀'}
                        </span>
                        <span className="text-[14.5px] font-bold text-[#0f172a] truncate">
                          {stu.student_name || stu.name || '未知学生'}
                        </span>
                        <span className="text-[11px] text-[#94a3b8] truncate">
                          ID: {stu.education_id || stu.student_no || '-'}
                        </span>
                      </div>

                      <div className="text-[11.5px] text-[#64748b] mt-0.5 truncate">
                        {stu.grade || ''} · {stu.class_name || '主校区'}
                      </div>

                      {/* 完成进度条 */}
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="w-24 bg-[#f1f5f9] h-1.5 rounded-full overflow-hidden">
                          <div className="bg-[#056b4f] h-full rounded-full" style={{ width: progressPct }} />
                        </div>
                        <span className="text-[10px] text-[#94a3b8]">{completed}/{total} 项</span>
                      </div>
                    </div>

                    {/* 得分与评定等级 */}
                    <div className="text-right pl-2">
                      <div className="text-[16px] font-extrabold text-[#0f172a]">{displayScore}</div>
                      <div className="mt-0.5">{getGradeBadge(displayGrade)}</div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        )}
      </div>

      {/* 统一通用选择器弹窗 (标准 Vant 底部滚轮抽屉) */}
      <Popup
        visible={pickerModalType !== null}
        position="bottom"
        round
        onClose={() => setPickerModalType(null)}
      >
        <Picker
          title={pickerTitle}
          columns={pickerColumns}
          onConfirm={(val) => handlePickerConfirm(val)}
          onCancel={() => setPickerModalType(null)}
        />
      </Popup>
</div>
  )
}
